import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircleIcon, CheckCircle2Icon } from 'lucide-react';
import { company } from '../data/company';
type Fields = {
  name: string;
  email: string;
  order: string;
  message: string;
};
type Errors = Partial<Record<keyof Fields, string>>;
type Status = 'idle' | 'opened' | 'unconfigured';
export function ContactForm() {
  const [fields, setFields] = useState<Fields>({
    name: '',
    email: '',
    order: '',
    message: ''
  });
  const [errors, setErrors] = useState<Errors>({});
  const [status, setStatus] = useState<Status>('idle');
  const update = (key: keyof Fields) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFields(f => ({
      ...f,
      [key]: e.target.value
    }));
    if (errors[key]) setErrors(er => ({
      ...er,
      [key]: undefined
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const next = validate(fields);
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    if (company.email.includes('{{')) {
      setStatus('unconfigured');
      return;
    }
    const subject = encodeURIComponent(`Inquiry from ${fields.name}${fields.order ? ` — Order ${fields.order}` : ''}`);
    const body = encodeURIComponent(`${fields.message}\n\n${fields.name}\n${fields.email}`);
    window.location.href = `mailto:${company.email}?subject=${subject}&body=${body}`;
    setStatus('opened');
  };
  return <form noValidate onSubmit={handleSubmit} className="rounded-xl border border-line bg-paper p-6 md:p-8" aria-labelledby="form-heading" data-id="element-10" data-mp="36nvp6is4v7q9">
      <h3 id="form-heading" className="font-serif text-2xl font-semibold text-ink" data-id="element-11" data-mp="3gkqj0odgojy7">Send a message</h3>
      <div className="mt-6 grid gap-5 sm:grid-cols-2" data-id="element-12" data-mp="3kltznd3c2k0h">
        <Field id="name" label="Name" value={fields.name} onChange={update('name')} error={errors.name} autoComplete="name" data-id="element-13" data-mp="3knetf0htpyxa" />
        <Field id="email" label="Email" type="email" value={fields.email} onChange={update('email')} error={errors.email} autoComplete="email" data-id="element-14" data-mp="vwlxjxnc6b1s" />
      </div>
      <div className="mt-5" data-id="element-15" data-mp="2wv60hykzeb52">
        <Field id="order" label="Order number (optional)" value={fields.order} onChange={update('order')} data-id="element-16" data-mp="2wylhj6y7tu94" />
      </div>
      <div className="mt-5" data-id="element-17" data-mp="35x6w6r0kgp4m">
        <label htmlFor="message" className="block text-sm font-medium text-ink" data-id="element-18" data-mp="35xfcksvweckk">Message</label>
        <textarea id="message" rows={5} value={fields.message} onChange={update('message')} aria-invalid={!!errors.message} aria-describedby={errors.message ? 'message-error' : undefined} className={inputClass(!!errors.message)} data-id="element-19" data-mp="30xubocx63u57" />
        {errors.message && <p id="message-error" className="mt-1.5 text-sm text-red-400" data-id="element-20" data-mp="2mzvz9d2eq7sc">{errors.message}</p>}
      </div>

      {status === 'opened' && <p role="status" className="mt-5 flex items-start gap-2 rounded-md bg-accent-soft p-3 text-sm text-ink" data-id="element-21" data-mp="2i5ru8zcpc9tw">
          <CheckCircle2Icon className="mt-0.5 h-4 w-4 shrink-0 text-accent" aria-hidden="true" data-id="element-22" data-mp="1pj6zdox0pfll" />
          Your email app should now be open with your message ready to send.
        </p>}
      {status === 'unconfigured' && <p role="alert" className="mt-5 flex items-start gap-2 rounded-md border border-placeholder-line bg-placeholder p-3 text-sm text-placeholder-ink" data-id="element-23" data-mp="9smuuehhszh4">
          <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-24" data-mp="zazfzvvar4f" />
          The company contact email hasn't been configured yet, so this message can't be sent.
        </p>}

      <button type="submit" className="mt-6 w-full rounded-md bg-accent px-5 py-3 text-sm font-semibold text-paper transition-colors duration-150 hover:bg-accent-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 sm:w-auto" data-id="element-25" data-mp="sqh1ilv5eywc">
        Send message
      </button>
      <p className="mt-4 text-xs text-muted" data-id="element-26" data-mp="2zgnj514kwtri">
        We use your details only to reply. See our{' '}
        <Link to="/policies#privacy" className="underline underline-offset-2 hover:text-ink" data-id="element-27" data-mp="34g9vl8cl25z9">Privacy Policy</Link>.
      </p>
    </form>;
}
interface FieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  error?: string;
  type?: string;
  autoComplete?: string;
}
function Field({
  id,
  label,
  value,
  onChange,
  error,
  type = 'text',
  autoComplete
}: FieldProps) {
  return <div data-id="element-28" data-mp="35w5ltwo39mua">
      <label htmlFor={id} className="block text-sm font-medium text-ink" data-id="element-29" data-mp="35wfe5qwlw635">{label}</label>
      <input id={id} type={type} value={value} onChange={onChange} autoComplete={autoComplete} aria-invalid={!!error} aria-describedby={error ? `${id}-error` : undefined} className={inputClass(!!error)} data-id="element-30" data-mp="6h0r4ype7g0o" />
      {error && <p id={`${id}-error`} className="mt-1.5 text-sm text-red-400" data-id="element-31" data-mp="3oohwd6owbufi">{error}</p>}
    </div>;
}
function inputClass(hasError: boolean) {
  return `mt-1.5 block w-full rounded-md border bg-paper px-3 py-2.5 text-sm text-ink transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-accent/30 ${hasError ? 'border-red-400 focus:border-red-400' : 'border-line focus:border-accent'}`;
}
function validate(f: Fields): Errors {
  const e: Errors = {};
  if (!f.name.trim()) e.name = 'Please enter your name.';
  if (!f.email.trim()) e.email = 'Please enter your email.';else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email)) e.email = 'Please enter a valid email address.';
  if (!f.message.trim()) e.message = 'Please enter a message.';
  return e;
}